import {
  FulfillmentActionNeeded,
  OrderDetailsModel,
  OrderFulfillmentStatusEnum,
  OrderTypeEnum,
} from '../../../../interfaces/order.interface';
import { routeLabel } from './fulfillment';

// Shared badge palette (escalation: green = done/good, amber = in flight / needs attention,
// red = blocked, gray = neutral/terminal, blue = informational).
export type Tone = 'green' | 'red' | 'amber' | 'gray' | 'blue';
export const toneClass: Record<Tone, string> = {
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  amber: 'bg-amber-100 text-amber-800',
  gray: 'bg-gray-200 text-gray-700',
  blue: 'bg-storyblue/10 text-storyblue',
};

// Single label+tone source for the canonical fulfillment status — used by the order list, the
// detail card, and the recovery report so the same status never renders two different ways.
export const FULFILLMENT_STATUS_BADGE: Record<OrderFulfillmentStatusEnum, { label: string; tone: Tone }> = {
  [OrderFulfillmentStatusEnum.NOT_APPLICABLE]: { label: '—', tone: 'gray' },
  [OrderFulfillmentStatusEnum.AWAITING_AUTHORIZATION]: { label: 'Awaiting teacher ship', tone: 'blue' },
  [OrderFulfillmentStatusEnum.NEEDS_ADDRESS]: { label: 'Missing address', tone: 'red' },
  [OrderFulfillmentStatusEnum.PREPARING_ASSETS]: { label: 'Preparing', tone: 'amber' },
  [OrderFulfillmentStatusEnum.READY_TO_DISPATCH]: { label: 'Ready to dispatch', tone: 'amber' },
  [OrderFulfillmentStatusEnum.DISPATCHED]: { label: 'Dispatched', tone: 'blue' },
  [OrderFulfillmentStatusEnum.IN_PRODUCTION]: { label: 'In production', tone: 'blue' },
  [OrderFulfillmentStatusEnum.SHIPPED]: { label: 'Shipped', tone: 'green' },
  [OrderFulfillmentStatusEnum.ERROR]: { label: 'Error', tone: 'red' },
  [OrderFulfillmentStatusEnum.CANCELED]: { label: 'Canceled', tone: 'gray' },
};

// Tolerant lookup: an unknown/missing status renders as a neutral raw value, never crashes.
export const fulfillmentStatusBadge = (status?: string | null): { label: string; tone: Tone } =>
  (status && FULFILLMENT_STATUS_BADGE[status as OrderFulfillmentStatusEnum]) || {
    label: status ? status.replace(/_/g, ' ') : '—',
    tone: 'gray',
  };

// ——— Display grouping (WooCommerce-simple groups; precise stage kept as a subtitle) ———
// Storage keeps the 10 precise statuses (they power stuck thresholds + diagnostics); the UI
// presents five groups so the list reads as a funnel: Waiting · Needs attention · In progress ·
// Shipped · Canceled.
export type FulfillmentGroup = 'waiting' | 'needs_attention' | 'in_progress' | 'shipped' | 'canceled' | 'other';

const GROUP_BADGE: Record<FulfillmentGroup, { label: string; tone: Tone }> = {
  waiting: { label: 'Waiting', tone: 'blue' },
  needs_attention: { label: 'Needs attention', tone: 'red' },
  in_progress: { label: 'In progress', tone: 'blue' },
  shipped: { label: 'Shipped', tone: 'green' },
  canceled: { label: 'Canceled', tone: 'gray' },
  other: { label: '—', tone: 'gray' },
};

// Declaration order matters: it drives the option order of the derived status filter dropdown
// (GROUPED_STATUS_OPTIONS below).
const STATUS_GROUPING: Record<OrderFulfillmentStatusEnum, { group: FulfillmentGroup; stage?: string }> = {
  [OrderFulfillmentStatusEnum.AWAITING_AUTHORIZATION]: { group: 'waiting', stage: 'teacher authorization' },
  [OrderFulfillmentStatusEnum.ERROR]: { group: 'needs_attention', stage: 'send failed' },
  [OrderFulfillmentStatusEnum.NEEDS_ADDRESS]: { group: 'needs_attention', stage: 'missing address' },
  [OrderFulfillmentStatusEnum.PREPARING_ASSETS]: { group: 'in_progress', stage: 'preparing assets' },
  [OrderFulfillmentStatusEnum.READY_TO_DISPATCH]: { group: 'in_progress', stage: 'ready to dispatch' },
  [OrderFulfillmentStatusEnum.DISPATCHED]: { group: 'in_progress', stage: 'sent to printer' },
  [OrderFulfillmentStatusEnum.IN_PRODUCTION]: { group: 'in_progress', stage: 'printing' },
  [OrderFulfillmentStatusEnum.SHIPPED]: { group: 'shipped' },
  [OrderFulfillmentStatusEnum.CANCELED]: { group: 'canceled' },
  [OrderFulfillmentStatusEnum.NOT_APPLICABLE]: { group: 'other' },
};

/** Group badge + precise stage subtitle for a canonical status. Tolerant of unknown values. */
export const fulfillmentGroupBadge = (
  status?: string | null
): { label: string; tone: Tone; stage?: string } => {
  const grouping = status && STATUS_GROUPING[status as OrderFulfillmentStatusEnum];
  if (!grouping) {
    return { label: status ? status.replace(/_/g, ' ') : '—', tone: 'gray' };
  }
  return { ...GROUP_BADGE[grouping.group], stage: grouping.stage };
};

// Dropdown heading per group — shipped/canceled/other collapse under one 'Terminal' heading.
const GROUP_DROPDOWN_HEADING: Record<FulfillmentGroup, string> = {
  waiting: 'Waiting',
  needs_attention: 'Needs attention',
  in_progress: 'In progress',
  shipped: 'Terminal',
  canceled: 'Terminal',
  other: 'Terminal',
};

/** Precise statuses per group — powers the grouped status filter dropdown. Derived from
 * STATUS_GROUPING so a new enum status shows up here automatically. */
export const GROUPED_STATUS_OPTIONS: Array<{ group: string; statuses: OrderFulfillmentStatusEnum[] }> =
  Object.entries(STATUS_GROUPING).reduce<Array<{ group: string; statuses: OrderFulfillmentStatusEnum[] }>>(
    (options, [status, { group }]) => {
      const heading = GROUP_DROPDOWN_HEADING[group];
      const bucket = options.find((o) => o.group === heading);
      if (bucket) bucket.statuses.push(status as OrderFulfillmentStatusEnum);
      else options.push({ group: heading, statuses: [status as OrderFulfillmentStatusEnum] });
      return options;
    },
    [],
  );

/** Readable dropdown label for a status option. Badge labels are reused, except the ones that
 * render as '—' (not_applicable) — unreadable as a filter option — which fall back to the
 * humanized raw status ('not applicable'). */
export const fulfillmentStatusOptionLabel = (status: OrderFulfillmentStatusEnum): string => {
  const { label } = fulfillmentStatusBadge(status);
  return label === '—' ? status.replace(/_/g, ' ') : label;
};

// "Do this now" chip rendered next to the status. 'none' renders nothing.
export const ACTION_NEEDED_BADGE: Record<Exclude<FulfillmentActionNeeded, 'none'>, { label: string; tone: Tone }> = {
  fix_address: { label: 'Fix address', tone: 'red' },
  investigate: { label: 'Investigate', tone: 'red' },
  dispatch: { label: 'Dispatch', tone: 'amber' },
  stuck: { label: 'Stuck', tone: 'red' },
};

export const actionNeededBadge = (action?: FulfillmentActionNeeded | null): { label: string; tone: Tone } | null =>
  action && action !== 'none' ? ACTION_NEEDED_BADGE[action] || null : null;

// Physical orders are the ones that actually ship and have a fulfillment route / sync state.
export const PHYSICAL_ORDER_TYPES: string[] = [
  OrderTypeEnum.BOOK_PURCHASE,
  OrderTypeEnum.PRE_ORDER_BOOK,
  OrderTypeEnum.BULK_PRINT,
];

export const isPhysicalOrder = (orderType?: string | null): boolean =>
  !!orderType && PHYSICAL_ORDER_TYPES.includes(orderType);

// Human label for an order type (covers types the legacy enum didn't include).
export const orderTypeLabel = (orderType?: string | null): string => {
  switch (orderType) {
    case OrderTypeEnum.BOOK_PURCHASE:
      return 'Book';
    case OrderTypeEnum.PRE_ORDER_BOOK:
      return 'Pre-order book';
    case OrderTypeEnum.BULK_PRINT:
      return 'Classroom release';
    case OrderTypeEnum.SUBSCRIPTION_PURCHASE:
      return 'Subscription';
    case OrderTypeEnum.CREDITS_PURCHASE:
      return 'Image credits';
    default:
      return orderType ? orderType.replace(/_/g, ' ') : '-';
  }
};

// What the order is FOR — book title, plan, credits, or bulk summary — for the "Item" column.
export const orderItemLabel = (order: OrderDetailsModel): string => {
  switch (order?.orderType) {
    case OrderTypeEnum.SUBSCRIPTION_PURCHASE:
      return order?.extraInfo?.plan ? `Plan: ${order.extraInfo.plan}` : 'Subscription';
    case OrderTypeEnum.CREDITS_PURCHASE: {
      const credits = order?.extraInfo?.credits ?? order?.quantity ?? 0;
      return `${credits} image credits`;
    }
    case OrderTypeEnum.BULK_PRINT: {
      const count = order?.totalBooksInBulk ?? order?.bulkPrintOrderNumbers?.length;
      const who = order?.classroomName ? `${order.classroomName} — ` : '';
      return count ? `${who}${count} book${count === 1 ? '' : 's'}` : `${who}Classroom release`;
    }
    default:
      // book / pre_order_book
      return order?.bookName || order?.bookId || '-';
  }
};

// Single order-total formula, shared by the index, the Customer Details list, and the detail card
// so the same order never shows two different totals. (Extracted verbatim from the prior list calc.)
export const calculateOrderTotal = (order: OrderDetailsModel): string => {
  const bookCost = +(order?.price || 0) + +(order?.additionalPageCost || 0);
  const qty = order?.orderType === OrderTypeEnum.BOOK_PURCHASE ? +(order?.quantity ?? 0) : 1;
  let total = (bookCost * qty + +(order?.shippingAmount ?? 0) - +(order?.couponDiscount || 0)).toFixed(2);
  const subTotal = +(order?.price || 0) + +(order?.additionalPageCost || 0) + +(order?.shippingAmount || 0);
  if (subTotal <= 0) {
    total = '0';
  }
  return total;
};

export type SyncState = 'synced' | 'pending' | 'error' | 'bundle' | 'na';

// Fulfillment sync state of a physical order:
//  - synced:  reached its provider (US → has shipstationOrderId; non-US → has luluPrintJobId)
//  - error:   a send was attempted and recorded a failure (shipstationSyncFailedAt / lastFulfillmentError)
//  - pending: not sent yet, no recorded error
//  - bundle:  a bulk-print aggregate — it never carries a provider id itself; its real sync state
//             lives on the individual child orders, so we don't claim synced/pending for the parent
//  - na:      non-physical order (subscription / credits)
export const getSyncState = (order: OrderDetailsModel): SyncState => {
  if (!isPhysicalOrder(order?.orderType)) {
    return 'na';
  }
  // Bulk-print parent is a container; the children carry the actual provider ids/sync state.
  if (order?.orderType === OrderTypeEnum.BULK_PRINT) {
    return 'bundle';
  }
  // Synced if it reached EITHER provider. Keying off the actual provider id (rather than the
  // country-derived expected route) keeps this correct even for mis-routed / unrecognized-country
  // orders that actually shipped via ShipStation when the route rule would have guessed Lulu.
  if (order?.shipstationOrderId || order?.luluPrintJobId) {
    return 'synced';
  }
  if (order?.lastFulfillmentError || order?.shipstationSyncFailedAt) {
    return 'error';
  }
  return 'pending';
};

// One label+tone table per sync state (same pattern as FULFILLMENT_STATUS_BADGE) — colors come
// from toneClass so a palette change reaches sync chips and status chips together.
export const SYNC_STATE_BADGE: Record<SyncState, { label: string; tone: Tone | null }> = {
  synced: { label: 'Synced', tone: 'green' },
  error: { label: 'Error', tone: 'red' },
  pending: { label: 'Pending', tone: 'amber' },
  bundle: { label: 'Bundle', tone: 'gray' },
  na: { label: '-', tone: null },
};

export const syncStateLabel = (s: SyncState): string => SYNC_STATE_BADGE[s]?.label ?? '-';

// Tailwind color classes for the sync badge (escalation: green → amber → red; bundle is neutral).
export const syncStateColor = (s: SyncState): string => {
  const tone = SYNC_STATE_BADGE[s]?.tone;
  return tone ? toneClass[tone] : 'text-gray-400';
};

// Print-PDF readiness chip ({label, tone} like the sibling maps). null → render nothing
// (not_required / absent state, i.e. non-print orders). Bulk parents DO get a chip: their
// printProcessingState is a live rollup the functions trigger maintains from the children.
export const printPdfBadge = (
  order?: { printProcessingState?: string | null; printProcessingError?: string | null } | null
): { label: string; tone: Tone; title: string } | null => {
  switch (order?.printProcessingState) {
    case 'completed':
      return { label: 'PDF ✓', tone: 'green', title: 'Print PDF ready' };
    case 'pending':
    case 'queued':
      return { label: 'PDF …', tone: 'amber', title: 'Print PDF generating' };
    case 'error':
      return {
        label: 'PDF ✗',
        tone: 'red',
        title: `Print PDF failed${order?.printProcessingError ? `: ${order.printProcessingError}` : ''}`,
      };
    default:
      return null;
  }
};

// ShipStation's own order status (awaiting_shipment / shipped / cancelled / …) → badge. Same
// {label, tone} convention as the sibling maps; unknown statuses humanize at tone blue.
export const providerStatusBadge = (s?: string | null): { label: string; tone: Tone } => {
  if (!s) return { label: 'Unknown', tone: 'gray' };
  const tones: Record<string, Tone> = {
    awaiting_shipment: 'amber',
    pending_fulfillment: 'amber',
    on_hold: 'amber',
    shipped: 'green',
    cancelled: 'gray',
    canceled: 'gray',
  };
  return { label: s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()), tone: tones[s] || 'blue' };
};

// Route label for physical orders only ('-' for subscription/credits). Reflects the ACTUAL provider:
// Lulu only when a Lulu print job id exists, otherwise ShipStation (the route everything ships
// through now). The detail view still shows the server-authoritative immediate-send route + any
// ambiguity flag.
export const orderRouteLabel = (order: OrderDetailsModel): string => {
  if (!isPhysicalOrder(order?.orderType)) {
    return '-';
  }
  return routeLabel(order);
};
